import { Card } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { useRef } from 'react';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';
import { TreemapChart } from 'echarts/charts';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type ReactEChartsCoreType from 'echarts-for-react/lib/core';
import type { EChartsOption } from 'echarts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {
  breakpointsMediaQuery,
  decoder,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import { useTooltipHide } from '@/hooks/useTooltipHide';
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  TreemapChart,
  CanvasRenderer
]);
type BubbleNode = {
  name?: string;
  value?: number | string;
  children?: BubbleNode[];
};

type BubbleChartResponse = {
  data?: BubbleNode[];
};

type BubbleTooltipParams = {
  name?: string;
  value?: number | string;
  data: BubbleNode;
};

type BubbleChartProps = {
  data?: BubbleChartResponse;
};

const getOptions = (
  data: BubbleNode[] | undefined,
  isMdUp: boolean
): EChartsOption => ({
  textStyle: {
    fontFamily: 'dana',
    color: getColor('gray-1100')
  },
  // نحوه نمایش تولتیپ نمودار
  tooltip: {
    triggerOn: isMdUp ? 'mousemove' : 'click',
    trigger: 'item',
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100'), fontSize: 14 },
    borderWidth: 1,
    formatter: (params: BubbleTooltipParams) => {
      return params.data.children
        ? ``
        : `<div>
          <div style="direction:rtl;" class="d-flex gap-1 justify-content-start" >
        <div class="d-flex flex-column flex-md-row gap-0 align-items-start align-items-md-center justify-content-between w-100 gap-1">     
          <span class="fw-bolder">
          متن پیوند: 
          </span>
          <span style="direction:ltr;"> ${params.name ? params.name : 'بدون متن پیوند'} </span>
          </div>
        </div>  
        <div class="d-flex flex-column flex-md-row gap-0 align-items-start align-items-md-center justify-content-between w-100 gap-1">     
        <span class="fw-bolder">
          تعداد استفاده:
          </span>
          ${params.value ? params.value : 'بدون مقدار'}
        </div>
      </div>`;
    },
    transitionDuration: 0,
    axisPointer: {
      type: 'none'
    }
  },
  grid: {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0
  },
  series: [
    {
      type: 'treemap',
      data: data,
      width: '100%',
      height: '100%',
      label: {
        color: getColor('gray-1000'),
        formatter: (params: BubbleTooltipParams) =>
          `${params.name ? params.name : 'بدون متن پیوند'}`
      },
      levels: [
        {
          color: [getColor('gray-100'), getColor('primary')],
          colorMappingBy: 'value',
          itemStyle: {
            borderWidth: 3,
            borderColor: '#333',
            gapWidth: 3
          }
        }
      ]
    }
  ]
} as unknown as EChartsOption);

const BubbleChart = ({ data }: BubbleChartProps) => {
  const chartRef = useRef<ReactEChartsCoreType | null>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);
  const nodes = data?.data?.map((node: BubbleNode) => ({
    // متن پیوندها در پاسخ API به صورت URI encoded برمی‌گردد.
    name: node.name ? decoder(node.name) : node.name,
    value: node.value
  }));

  const cardRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<HTMLDivElement | null>(null);
  return (
    <Card className="my-2" ref={cardRef}>
      <FalconCardHeader
        title={
          <div>
            متن پیوند{' '}
            <HelpTooltip html={crawlTooltip.crawlerInternalLinls.chart} />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={<CardChartDropDown cardRef={cardRef} chartRef={chartRef} />}
      />
      <Card.Body ref={svgRef} className="">
        {data && (
          <ReactEChartsCore
            ref={chartRef}
            echarts={echarts}
            option={getOptions(nodes, isMdUp)}
            style={{ height: '500px', width: '100%' }}
          />
        )}
      </Card.Body>
    </Card>
  );
};
export default BubbleChart;
